// Customer-facing check-in page — reached by scanning the shop's QR code.
// No auth required. Mobile-first, single-purpose.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { OrderStatus } from '@notify/shared';

import {
  type BrowserPushSubscription,
  type OrderStatusResult,
  type OrderValidation,
  checkinService,
} from '../services/checkin.service';

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = 'step1' | 'step2' | 'step3';

// ─── Session storage ──────────────────────────────────────────────────────────

const STORAGE_KEY = 'notify_checkin';

interface SavedCheckin {
  step: Step;
  order: OrderValidation;
  shopId: string;
  shopName: string;
  location: string;
  subscribed: boolean;
}

function readSavedCheckin(shopId: string): SavedCheckin | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as SavedCheckin;
    return saved.shopId === shopId ? saved : null;
  } catch {
    return null;
  }
}

function persistCheckin(data: SavedCheckin): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore quota/security errors
  }
}

function clearCheckin(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore
  }
}

function isOrderSubscribed(orderId: string): boolean {
  try {
    return sessionStorage.getItem(`notify_subscribed_${orderId}`) === 'true';
  } catch {
    return false;
  }
}

function markOrderSubscribed(orderId: string): void {
  try {
    sessionStorage.setItem(`notify_subscribed_${orderId}`, 'true');
  } catch {
    // Ignore
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function secondsUntil(deadline: string | null): number {
  if (!deadline) return 0;
  return Math.max(0, Math.floor((new Date(deadline).getTime() - Date.now()) / 1000));
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 22a2 2 0 001.995-1.85L14 20H10a2 2 0 001.995 1.85L12 22zM12 2a7 7 0 016.996 6.76L19 9v5l2 3v1H3v-1l2-3V9a7 7 0 017-7z" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg className="h-16 w-16 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: OrderStatus }) {
  const styles: Record<OrderStatus, string> = {
    [OrderStatus.PENDING]: 'bg-gray-100 text-gray-600',
    [OrderStatus.PREPARING]: 'bg-amber-100 text-amber-700',
    [OrderStatus.READY]: 'bg-green-100 text-green-700',
    [OrderStatus.COLLECTED]: 'bg-blue-100 text-blue-700',
    [OrderStatus.CANCELLED]: 'bg-red-100 text-red-500',
  };
  const labels: Record<OrderStatus, string> = {
    [OrderStatus.PENDING]: 'Pending',
    [OrderStatus.PREPARING]: 'Preparing',
    [OrderStatus.READY]: 'Ready to collect',
    [OrderStatus.COLLECTED]: 'Collected',
    [OrderStatus.CANCELLED]: 'Cancelled',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

// ─── Countdown ────────────────────────────────────────────────────────────────

function Countdown({ deadline }: { deadline: string | null }) {
  const [seconds, setSeconds] = useState(() => secondsUntil(deadline));

  useEffect(() => {
    setSeconds(secondsUntil(deadline));
    const id = setInterval(() => {
      setSeconds(secondsUntil(deadline));
    }, 1000);
    return () => clearInterval(id);
  }, [deadline]);

  if (!deadline) {
    return <p className="text-sm text-[#64748B]">Preparing your order…</p>;
  }

  if (seconds === 0) {
    return <p className="text-2xl font-bold text-red-500">Overdue</p>;
  }

  return (
    <p className={`text-4xl font-bold tabular-nums ${seconds < 60 ? 'text-red-500' : 'text-[#0F172A]'}`}>
      {formatCountdown(seconds)}
    </p>
  );
}

// ─── Step 1: Enter order reference ────────────────────────────────────────────

interface Step1Props {
  shopId: string;
  onSuccess: (order: OrderValidation) => void;
}

function Step1EnterOrder({ shopId, onSuccess }: Step1Props) {
  const [ref, setRef] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = ref.trim();
    if (!trimmed) return;
    setLoading(true);
    setError('');
    try {
      const order = await checkinService.validateOrder(shopId, trimmed);
      if (
        order.status === OrderStatus.COLLECTED ||
        order.status === OrderStatus.CANCELLED
      ) {
        setError('This order is no longer active.');
        return;
      }
      onSuccess(order);
    } catch {
      setError("We couldn't find that order. Please check your number and try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="animate-fade-in space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-[#0F172A]">Enter your order number</h2>
        <p className="mt-1 text-sm text-[#64748B]">Find it on your receipt or order ticket</p>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <input
          ref={inputRef}
          type="text"
          inputMode="text"
          autoComplete="off"
          placeholder="#042"
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          className="block w-full rounded-xl border border-gray-200 px-4 py-4 text-center text-2xl font-mono font-bold text-[#0F172A] placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
        />
        {error && (
          <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-600">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading || !ref.trim()}
          className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-[#6366F1] px-4 py-3 text-base font-semibold text-white hover:bg-indigo-600 disabled:opacity-60 transition-colors"
        >
          {loading ? (
            <>
              <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Checking…
            </>
          ) : (
            'Track My Order'
          )}
        </button>
      </form>
    </div>
  );
}

// ─── Step 2: Enable notifications ────────────────────────────────────────────

interface Step2Props {
  order: OrderValidation;
  onSubscribed: () => void;
  onSkip: () => void;
}

function Step2EnableNotifications({ order, onSubscribed, onSkip }: Step2Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [denied, setDenied] = useState(false);

  // Live status — polled every 10 s so the badge updates before the user enables notifications
  const [liveStatus, setLiveStatus] = useState<{ status: OrderStatus; prepDeadline: string | null }>({
    status: order.status,
    prepDeadline: order.prepDeadline,
  });
  const [lastPollAt, setLastPollAt] = useState<Date | null>(null);
  const [secsAgo, setSecsAgo] = useState(0);
  const pollingRef = useRef(false);

  const pollStatus = useCallback(async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    try {
      const result = await checkinService.getOrderStatus(order.orderId);
      setLiveStatus({ status: result.status, prepDeadline: result.prepDeadline });
      setLastPollAt(new Date());
      setSecsAgo(0);
    } catch {
      // Silent — keep showing last known state
    } finally {
      pollingRef.current = false;
    }
  }, [order.orderId]);

  // Poll immediately on mount, then every 10 s
  useEffect(() => {
    void pollStatus();
    const id = setInterval(() => void pollStatus(), 15_000);
    return () => clearInterval(id);
  }, [pollStatus]);

  // Tick the "X seconds ago" counter every second
  useEffect(() => {
    if (!lastPollAt) return;
    const id = setInterval(() => {
      setSecsAgo(Math.floor((Date.now() - lastPollAt.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [lastPollAt]);

  async function handleEnable() {
    setLoading(true);
    setError('');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setDenied(true);
        return;
      }

      const vapidKey = await checkinService.getVapidKey();
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none'
      })
      await navigator.serviceWorker.ready
      await registration.update()
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey).buffer as ArrayBuffer,
      });

      const raw = pushSubscription.toJSON();
      const sub: BrowserPushSubscription = {
        endpoint: pushSubscription.endpoint,
        keys: {
          p256dh: raw.keys?.p256dh ?? '',
          auth: raw.keys?.auth ?? '',
        },
      };

      await checkinService.subscribe(order.orderId, sub);

      // Keep service worker alive with a periodic ping
      setInterval(() => {
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'PING' })
        }
      }, 20000)

      // Persist subscription flag so reloads skip straight to Step 3
      markOrderSubscribed(order.orderId);
      onSubscribed();
    } catch (err) {
      if (err instanceof Error && err.name === 'NotAllowedError') {
        setDenied(true);
      } else {
        setError('Something went wrong setting up notifications. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="animate-fade-in space-y-6">
      {/* Order details */}
      <div className="rounded-xl border border-gray-200 bg-[#F8FAFC] p-4 text-center space-y-3">
        <p className="font-mono text-2xl font-bold text-[#6366F1]">{order.orderReference}</p>
        <StatusBadge status={liveStatus.status} />
        <div>
          <p className="mb-1 text-xs uppercase tracking-widest text-[#64748B]">Ready in</p>
          <Countdown deadline={liveStatus.prepDeadline} />
        </div>
        <div className="flex items-center justify-center gap-3">
          {lastPollAt && (
            <p className="text-xs text-[#94A3B8]">
              Updated {secsAgo === 0 ? 'just now' : `${secsAgo}s ago`}
            </p>
          )}
          <button
            type="button"
            onClick={() => void pollStatus()}
            className="text-xs text-[#6366F1] hover:underline"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center">
        <h2 className="text-xl font-semibold text-[#0F172A]">Get notified when your food is ready</h2>
        <p className="mt-1 text-sm text-[#64748B]">
          We'll send a notification to this device when your order is ready to collect.
        </p>
      </div>

      {denied ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-700 text-center">
          You've blocked notifications. Please enable them in your browser settings to get notified.
        </div>
      ) : error ? (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-600">
          {error}
        </p>
      ) : null}

      <div className="space-y-3">
        <button
          type="button"
          disabled={loading}
          onClick={() => void handleEnable()}
          className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-[#6366F1] px-4 py-3 text-base font-semibold text-white hover:bg-indigo-600 disabled:opacity-60 transition-colors"
        >
          {loading ? (
            <>
              <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Setting up…
            </>
          ) : (
            <>
              <BellIcon className="h-5 w-5" />
              Enable Notifications
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onSkip}
          className="w-full py-2 text-sm text-[#64748B] hover:text-[#0F172A] transition-colors"
        >
          No thanks, I'll check manually
        </button>
      </div>
    </div>
  );
}

// ─── Step 3: Tracking screen ──────────────────────────────────────────────────

interface Step3Props {
  orderId: string;
  initialOrder: OrderValidation;
  shopName: string;
  subscribed: boolean;
  onOrderComplete: () => void;
}

const playNotificationSound = () => {
  try {
    const audio = new Audio('/notification.mp3')
    audio.volume = 1.0
    audio.play().catch(() => {
      // Try mp3 as fallback
      const audio2 = new Audio('/notification')
      audio2.volume = 1.0
      audio2.play().catch(() => {})
    })
  } catch {
    // Audio not supported
  }
}

function Step3Tracking({ orderId, initialOrder, shopName, subscribed, onOrderComplete }: Step3Props) {
  const [liveStatus, setLiveStatus] = useState<OrderStatusResult>({
    status: initialOrder.status,
    prepDeadline: initialOrder.prepDeadline,
    notifiedAt: null,
  });
  const [lastPollAt, setLastPollAt] = useState<Date | null>(null);
  const [secsAgo, setSecsAgo] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onOrderCompleteRef = useRef(onOrderComplete);
  onOrderCompleteRef.current = onOrderComplete;
  const pollingRef = useRef(false);
  const prevStatusRef = useRef<OrderStatus>(initialOrder.status);

  const poll = useCallback(async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    try {
      const result = await checkinService.getOrderStatus(orderId);
      if (result.status === OrderStatus.READY && prevStatusRef.current !== OrderStatus.READY) {
        playNotificationSound()
      }
      prevStatusRef.current = result.status;
      setLiveStatus(result);
      setLastPollAt(new Date());
      setSecsAgo(0);
      if (result.status === OrderStatus.COLLECTED || result.status === OrderStatus.CANCELLED) {
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        onOrderCompleteRef.current();
      }
    } catch {
      // Silent — keep showing last known state
    } finally {
      pollingRef.current = false;
    }
  }, [orderId]);

  // Poll immediately on mount, then every 20 s
  useEffect(() => {
    void poll();
    intervalRef.current = setInterval(() => void poll(), 20_000);
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, [poll]);

  // Tick the "X seconds ago" counter every second
  useEffect(() => {
    if (!lastPollAt) return;
    const id = setInterval(() => {
      setSecsAgo(Math.floor((Date.now() - lastPollAt.getTime()) / 1500));
    }, 1500);
    return () => clearInterval(id);
  }, [lastPollAt]);

  const isReady = liveStatus.status === OrderStatus.READY;
  const isCollected = liveStatus.status === OrderStatus.COLLECTED;

  return (
    <div className="animate-fade-in space-y-6">
      {/* Confirmation */}
      <div className="flex flex-col items-center text-center gap-3">
        <div className="animate-bounce-in">
          <CheckCircleIcon />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-[#0F172A]">You're all set!</h2>
          <p className="mt-1 text-sm text-[#64748B]">
            {subscribed
              ? "We'll notify you on this device when your food is ready. You can close this page."
              : 'Check back here to see when your food is ready.'}
          </p>
        </div>
      </div>

      {/* Live status card */}
      <div className="rounded-xl border border-gray-200 bg-[#F8FAFC] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <p className="font-mono text-xl font-bold text-[#6366F1]">{initialOrder.orderReference}</p>
          <StatusBadge status={liveStatus.status} />
        </div>

        {isCollected ? (
          <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-4 text-center">
            <p className="text-2xl mb-1">✅</p>
            <p className="font-semibold text-blue-700">Order collected. Enjoy your meal!</p>
          </div>
        ) : isReady ? (
          <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-4 text-center animate-bounce-in">
            <p className="text-3xl mb-2">🍽️</p>
            <p className="text-lg font-bold text-green-700">Your food is ready!</p>
            <p className="mt-1 text-sm text-green-600">Please collect your order from {shopName}</p>
          </div>
        ) : (
          <div className="text-center">
            <p className="mb-1 text-xs uppercase tracking-widest text-[#64748B]">Ready in</p>
            <Countdown deadline={liveStatus.prepDeadline} />
          </div>
        )}

        <div className="flex items-center justify-center gap-3 pt-1">
          {lastPollAt && (
            <p className="text-xs text-[#94A3B8]">
              Updated {secsAgo === 0 ? 'just now' : `${secsAgo}s ago`}
            </p>
          )}
          <button
            type="button"
            onClick={() => void poll()}
            className="text-xs text-[#6366F1] hover:underline"
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ShopCheckinPage() {
  const { shopId } = useParams<{ shopId: string }>();
  const [searchParams] = useSearchParams();

  // URL query params are the source of truth for shop info — present on every load
  // since the URL doesn't change. sessionStorage is a fallback for edge cases
  // where the user navigates here without query params (e.g. history.back()).
  const urlShopName = searchParams.get('name') ?? '';
  const urlLocation = searchParams.get('location') ?? '';

  // Read saved session once — only used to initialise state on first render.
  // useRef ensures we don't re-read storage on every render.
  const savedRef = useRef(shopId ? readSavedCheckin(shopId) : null);
  const saved = savedRef.current;

  const [fetchedShopName, setFetchedShopName] = useState('');
  const [fetchedLocation, setFetchedLocation] = useState('');

  // If the URL is missing the name param and we have no saved session data,
  // fetch public shop info from the API (no auth required).
  useEffect(() => {
    if (!shopId || urlShopName || saved?.shopName) return;
    void checkinService.getShopPublicInfo(shopId).then(({ shopName, location }) => {
      setFetchedShopName(shopName);
      setFetchedLocation(location);
    }).catch(() => {
      // Silent — fallback text 'This shop' will be shown
    });
  }, [shopId, urlShopName, saved?.shopName]);

  // If the saved order already has a subscription flag, jump straight to Step 3.
  const alreadySubscribed = saved?.order
    ? isOrderSubscribed(saved.order.orderId)
    : false;

  const [step, setStep] = useState<Step>(
    alreadySubscribed ? 'step3' : (saved?.step ?? 'step1')
  );
  const [order, setOrder] = useState<OrderValidation | null>(saved?.order ?? null);
  const [subscribed, setSubscribed] = useState<boolean>(
    alreadySubscribed || (saved?.subscribed ?? false)
  );

  // Prefer live URL params; fall back to saved session, then API fetch, then placeholder
  const shopName = urlShopName || saved?.shopName || fetchedShopName || 'This shop';
  const location = urlLocation || saved?.location || fetchedLocation;

  // Persist current state to sessionStorage whenever it changes
  useEffect(() => {
    if (!shopId || !order) return;
    persistCheckin({ step, order, shopId, shopName, location, subscribed });
  }, [step, order, subscribed, shopId, shopName, location]);

  if (!shopId) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#F8FAFC] px-4 text-center">
        <BellIcon className="h-12 w-12 text-[#6366F1]" />
        <p className="mt-4 text-base font-medium text-[#0F172A]">Invalid check-in link.</p>
        <p className="mt-1 text-sm text-[#64748B]">Please scan the QR code at the shop again.</p>
      </div>
    );
  }

  function handleOrderFound(foundOrder: OrderValidation) {
    setOrder(foundOrder);
    setStep('step2');
  }

  function handleSubscribed() {
    setSubscribed(true);
    setStep('step3');
  }

  function handleSkip() {
    setSubscribed(false);
    setStep('step3');
  }

  function handleOrderComplete() {
    // Order is collected or cancelled — clear stored session so the next
    // customer who scans this QR starts fresh.
    clearCheckin();
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-8">
      <div className="mx-auto w-full max-w-[420px]">
        {/* Header */}
        <div className="mb-8 flex flex-col items-center text-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#6366F1] animate-pulse-slow">
            <BellIcon className="h-8 w-8 text-white" />
          </div>
          <p className="text-xl font-bold text-[#0F172A] tracking-tight">Notify</p>
        </div>

        {/* Shop info card */}
        <div className="mb-6 rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
          <p className="text-lg font-bold text-[#0F172A]">{shopName}</p>
          {location && (
            <div className="mt-1 flex items-center gap-1.5 text-sm text-[#64748B]">
              <PinIcon />
              <span>{location}</span>
            </div>
          )}
        </div>

        {/* Step content */}
        <div className="rounded-2xl border border-gray-200 bg-white px-6 py-6 shadow-sm">
          {step === 'step1' && (
            <Step1EnterOrder shopId={shopId} onSuccess={handleOrderFound} />
          )}
          {step === 'step2' && order && (
            <Step2EnableNotifications
              order={order}
              onSubscribed={handleSubscribed}
              onSkip={handleSkip}
            />
          )}
          {step === 'step3' && order && (
            <Step3Tracking
              orderId={order.orderId}
              initialOrder={order}
              shopName={shopName}
              subscribed={subscribed}
              onOrderComplete={handleOrderComplete}
            />
          )}
        </div>

        <p className="mt-6 text-center text-xs text-[#94A3B8]">
          Powered by Notify · One notification per order, no spam ever.
        </p>
      </div>
    </div>
  );
}
